/**
 * components/client-detail/DossierActionSidebar.tsx
 *
 * Right-side action sidebar for the dossier builder (3-column single-screen layout).
 * Sections:
 *  1. Live summary - piece count + file count
 *  2. Stamp toggle with tooltip (position=right to avoid overflow clipping) and optional preview
 *  3. Three independent download cards (dossier PDF, bordereau PDF, bordereau Word)
 *
 * Desktop (>= lg): fixed ~272px panel, border-l, full-height scrollable.
 * Mobile (< lg): FAB (bottom-right, z-40) opens an animated bottom-sheet overlay.
 *
 * Updated Apr 2026:
 *  - Label "Tampon" renamed to "Ajouter le tampon"
 *  - Tooltip position changed to "right" to prevent overflow clipping
 *  - Stamp preview enlarged (size 96)
 *  - Removed redundant "Appliqué sur chaque page du dossier" caption
 * Updated Apr 2026: aria-label on mobile close button; Escape key + focus trap on mobile sheet.
 *   StampStatus extracted to src/types/dossier.ts (shared with useDossierBuilder).
 */

import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Download, AlertTriangle, FileText, HelpCircle, Loader2, X } from 'lucide-react';
import Switch from '../ui/Switch';
import { Tooltip } from '../ui/Tooltip';
import StampPreview from '../settings/StampPreview';
import DossierExportCard from './DossierExportCard';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import type { DossierPiece, StampStatus } from '../../types/dossier';
import type { ExportPhase } from '../../hooks/useDossierBuilder';

interface DossierActionSidebarProps {
  includedPieces: DossierPiece[];
  withStamp: boolean;
  stampStatus: StampStatus;
  isPreloading: boolean;
  dossierPdfPhase: ExportPhase;
  dossierPdfProgress: { done: number; total: number };
  bordereauPdfPhase: ExportPhase;
  bordereauDocPhase: ExportPhase;
  onToggleStamp: (val: boolean) => void;
  onDownloadDossierPdf: () => void;
  onDownloadBordereauPdf: () => void;
  onDownloadBordereauDoc: () => void;
}

interface ContentProps extends DossierActionSidebarProps {
  onClose?: () => void;
}

function SidebarContent({
  includedPieces,
  withStamp,
  stampStatus,
  isPreloading,
  dossierPdfPhase,
  dossierPdfProgress,
  bordereauPdfPhase,
  bordereauDocPhase,
  onToggleStamp,
  onDownloadDossierPdf,
  onDownloadBordereauPdf,
  onDownloadBordereauDoc,
  onClose,
}: ContentProps) {
  const stampControlDisabled = stampStatus.isLoading || stampStatus.isMissing || stampStatus.isIncomplete;
  const effectiveWithStamp = withStamp && !stampControlDisabled;
  const isEmpty = includedPieces.length === 0;
  const totalFiles = includedPieces.reduce((acc, p) => acc + p.docs.length, 0);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b border-neutral-100">
        <div className="flex items-center justify-between lg:hidden mb-3">
          <h2 className="text-base font-semibold text-neutral-900">Téléchargements</h2>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Fermer"
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-neutral-600" />
            </button>
          )}
        </div>

        <p className="hidden lg:block text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2">
          Résumé
        </p>
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-bold text-neutral-900 leading-none">
            {includedPieces.length}
          </span>
          <span className="text-sm text-neutral-500">
            pièce{includedPieces.length !== 1 ? 's' : ''}
          </span>
          <span className="text-neutral-300 mx-0.5">·</span>
          <span className="text-sm font-semibold text-neutral-700">{totalFiles}</span>
          <span className="text-sm text-neutral-500">
            fichier{totalFiles !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="px-4 pt-4 pb-3 space-y-3 border-b border-neutral-100">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-medium text-neutral-800">Ajouter le tampon</span>
              <Tooltip text="Numéroté et appliqué sur chaque page du dossier PDF" position="right">
                <HelpCircle className="w-3.5 h-3.5 text-neutral-400 cursor-help" />
              </Tooltip>
            </div>
            <Switch
              checked={effectiveWithStamp}
              onChange={onToggleStamp}
              disabled={stampControlDisabled}
            />
          </div>

          {effectiveWithStamp && stampStatus.config && (
            <div className="flex flex-col items-center pt-1">
              <StampPreview
                title={stampStatus.config.title}
                toqueNumber={stampStatus.config.toqueNumber}
                size={96}
              />
            </div>
          )}

          {stampStatus.isMissing && !stampStatus.isLoading && (
            <div className="flex items-start gap-2 text-xs text-neutral-500 bg-neutral-50 rounded-lg p-2.5 border border-neutral-200">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-warning-500" />
              <span>
                Tampon non configuré.{' '}
                <Link to="/settings#stamp" className="text-primary-600 underline">
                  Configurer
                </Link>
              </span>
            </div>
          )}

          {stampStatus.isIncomplete && !stampStatus.isLoading && (
            <div className="flex items-start gap-2 text-xs text-warning-700 bg-warning-50 rounded-lg p-2.5 border border-warning-200">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Tampon incomplet.{' '}
                <Link to="/settings#stamp" className="text-warning-700 underline">
                  Compléter
                </Link>
              </span>
            </div>
          )}
        </div>

        <div className="px-4 pt-4 pb-4 space-y-2.5">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">
              Téléchargements
            </p>
            {isPreloading && (
              <div className="flex items-center gap-1 text-[10px] text-neutral-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Préchargement…</span>
              </div>
            )}
          </div>

          <DossierExportCard
            icon={<FileText className="w-4 h-4" />}
            title="Dossier de pièces"
            subtitle={
              isEmpty
                ? 'Aucune pièce sélectionnée'
                : `${includedPieces.length} pièce${includedPieces.length !== 1 ? 's' : ''} · ${totalFiles} fichier${totalFiles !== 1 ? 's' : ''}`
            }
            phase={dossierPdfPhase}
            onDownload={onDownloadDossierPdf}
            disabled={isEmpty}
            progress={dossierPdfProgress}
          />

          <DossierExportCard
            icon={<FileText className="w-4 h-4" />}
            title="Bordereau"
            subtitle="Format PDF"
            phase={bordereauPdfPhase}
            onDownload={onDownloadBordereauPdf}
            disabled={isEmpty}
          />

          <DossierExportCard
            icon={<FileText className="w-4 h-4" />}
            title="Bordereau"
            subtitle="Format Word (.doc)"
            phase={bordereauDocPhase}
            onDownload={onDownloadBordereauDoc}
            disabled={isEmpty}
          />
        </div>
      </div>
    </div>
  );
}

export default function DossierActionSidebar(props: DossierActionSidebarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  useFocusTrap(sheetRef, isOpen);

  return (
    <>
      <div className="hidden lg:flex w-[272px] flex-shrink-0 border-l border-neutral-200 h-full overflow-hidden flex-col">
        <SidebarContent {...props} />
      </div>

      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="lg:hidden fixed bottom-4 right-6 w-14 h-14 bg-neutral-800 hover:bg-neutral-900 text-white rounded-full shadow-soft-lg hover:shadow-soft-xl transition-all flex items-center justify-center z-40"
        aria-label="Voir les options de téléchargement"
      >
        <Download className="w-6 h-6" />
      </button>

      {isOpen && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-neutral-900/50 z-40 animate-fade-in"
            onClick={() => setIsOpen(false)}
          />
          <div
            ref={sheetRef}
            className="lg:hidden fixed inset-x-0 bottom-0 top-20 bg-white rounded-t-2xl shadow-soft-xl z-50 animate-slide-up overflow-hidden flex flex-col"
            onKeyDown={(e) => { if (e.key === 'Escape') setIsOpen(false); }}
          >
            <SidebarContent {...props} onClose={() => setIsOpen(false)} />
          </div>
        </>
      )}
    </>
  );
}
